import React, { createContext, PropsWithChildren, useContext, useEffect } from 'react';
import { useFlags as useLaunchDarklyFlags, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import { Logger } from '@garner-health/logging';

export interface FeatureFlags {
  'membership-single-source-of-truth-in-eligibility'?: boolean;
}

interface UserIdentity {
  key: string;
  /** Defaults to user */
  kind?: string;
  [field: string]: any;
}

export interface Config {
  log: Logger;
  clientId: string;
  appId: string;
  appVersion: string;
  onFlagsChange: (flags: FeatureFlags) => void;
  defaults: FeatureFlags;
}

const FeatureFlagContext = createContext<Config | null>(null);

function useConfig(): Config {
  const config = useContext(FeatureFlagContext);
  if (!config) throw new Error('Feature flag provider has not been initialized');
  return config;
}

/**
 * Provider for feature flags.
 * NB: This provider must be rendered at the root of your react application.
 *
 * @example
 * const WrappedApp = () => (
 *   <React.StrictMode>
 *     <FeatureFlagProvider config={featureFlagConfig}>
 *       <App />
 *     </FeatureFlagProvider>
 *   </React.StrictMode>
 * );
 * createRoot(document.getElementById('approot')!).render(<WrappedApp />);
 */
export const FeatureFlagProvider = ({ children, config }: PropsWithChildren<{ config: Config }>) => {
  const { log, clientId, appId, appVersion } = config;

  const WrappedChildren = withLDProvider({
    reactOptions: { useCamelCaseFlagKeys: false },
    clientSideID: clientId,
    options: {
      logger: log.child({ module: 'launchDarkly' }),
      streaming: true, // allows new flags to be picked up as user navigates app
      application: { id: appId, version: appVersion },
    },
  })((() => children) as React.ComponentType<unknown>);

  return (
    <FeatureFlagContext.Provider value={config}>
      <WrappedChildren />
    </FeatureFlagContext.Provider>
  );
};

export function useFlags(): FeatureFlags {
  const ldFlags = useLaunchDarklyFlags<FeatureFlags>();
  const { log, defaults, onFlagsChange } = useConfig();

  useEffect(() => {
    onFlagsChange(ldFlags);
  }, [ldFlags, onFlagsChange]);

  return new Proxy<FeatureFlags>(ldFlags, {
    get(target: FeatureFlags, prop: keyof FeatureFlags) {
      const value = target[prop];
      if (value === undefined) {
        const defaultValue: unknown = prop in defaults ? defaults[prop] : undefined;
        log.warn(
          { strProp: prop, strDefault: defaultValue?.toString() },
          'Feature flag is undefined when being fetched, falling back to default',
        );
        return defaultValue;
      }
      return value;
    },
  });
}

export function useIdentifyForFeatureFlags(identify: () => UserIdentity | Promise<UserIdentity>, onDone: () => void) {
  const ldClient = useLDClient();
  const { log } = useConfig();

  useEffect(() => {
    if (!ldClient) {
      log.error('Feature flag client is null, cannot identify user');
      return;
    }
    (async function () {
      log.debug('Initializing user with LaunchDarkly');
      const userIdentity = await identify();
      ldClient
        .identify({ kind: 'user', ...userIdentity })
        .then(() => {
          onDone();
        })
        .catch(err => log.error(err, 'Error initializing feature flag user context'));
    })();
  }, [ldClient, identify, log, onDone]);
}
